/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { type ComponentPropsWithRef, useContext, useEffect } from 'react';
import { ViewStackView } from '../view-stack/view';
import { DrawerContext } from './context';
import styles from './styles.module.css';
import type { ViewStackViewProps } from '../view-stack/types';

/**
 * DrawerView - Content region for a specific drawer view.
 *
 * Each view is identified by a unique `id` and displayed when that
 * view is active in the stack. Contains DrawerHeader, DrawerContent,
 * and optionally DrawerFooter.
 *
 * @param props - ViewStackViewProps & ComponentPropsWithRef<'div'>
 * @param props.id - Unique identifier for the view.
 * @param props.children - Content to render inside the view.
 * @param props.className - Optional CSS class name.
 * @returns The rendered DrawerView component.
 *
 * @example
 * ```tsx
 * <DrawerView id={viewId}>
 *   <DrawerHeader title="Settings" />
 *   <DrawerContent>View content here</DrawerContent>
 *   <DrawerFooter>Footer actions</DrawerFooter>
 * </DrawerView>
 * ```
 */
export function DrawerView({
  id,
  children,
  className,
  ...rest
}: ViewStackViewProps & ComponentPropsWithRef<'div'>) {
  const { register, unregister } = useContext(DrawerContext);

  useEffect(() => {
    register(id);

    return () => unregister(id);
  }, [register, unregister, id]);

  return (
    <ViewStackView id={id}>
      <div {...rest} className={clsx(styles.view, className)} role='tabpanel'>
        {children}
      </div>
    </ViewStackView>
  );
}
